import React, { useState }  from 'react';
import { useAppDispatch } from 'store/store';
import { Card, ErrorMessage } from 'ui-toolkit';
import { AffidavitsTable } from 'components/order/documentsPortal/affidavits/AffidavitsTable';
import { getAdminDocuments } from 'store/order/documents/adminDocuments/dispatch';
import { useSelectCurrentOrder } from 'selectors/order/selectCurrentOrder';
import { checkForMutationSuccess } from '../../../API/checkForMutationSuccess';
import { useGeneratePackageMutation } from '../../../API/order/documents/DocumentPackageAPI';
import {
  defaultNormalizedDocumentCategoriesData,
  useListDocumentCategoriesQuery,
} from '../../../API/documentCategories/DocumentCategoriesApi';
import { eTransactionCategory } from '../../../ui-toolkit/enums/eTransactionType';
import { ePartyPurpose } from '../../../ui-toolkit/enums/ePartyPurpose';
import { convertToError } from '../../../API/BaseApi';
import { useHasFeatureFlag } from '../../../selectors/useHasFeatureFlag';

export const AffidavitsContainer: React.FC<{ setIsZDocLoading: (val: boolean) => void }> = ({ setIsZDocLoading }) => {
  const dispatch = useAppDispatch();
  const { data: order } = useSelectCurrentOrder();
  const { data: { allCategories } = defaultNormalizedDocumentCategoriesData } = useListDocumentCategoriesQuery();
  const orderId = order.transaction.id!;
  const [generatePackageMutation, { isLoading: isAffidavitGenerating, error: generateAffidavitError }] =
    useGeneratePackageMutation();
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const [generatingParty, setGeneratingParty] = useState<ePartyPurpose | null>(null);

  const onGenerateAffidavits = async (party: ePartyPurpose) => {
    try {
      const result = await generatePackageMutation({ orderId, packageType: `legacy-${party}-affidavit` });
      if (checkForMutationSuccess(result, `sending ${party} affidavit generation request`)) {
        await dispatch(getAdminDocuments(orderId));
      }
    } catch (e) {}
  };

  const onGenerateViaZdocs = async (party: ePartyPurpose) => {
    try {
      setGeneratingParty(party);
      // When Zillow Closing Feature Flag is enabled, use new package types
      // When disabled, use current package types
      let packageType: string;
      if (isZillowClosingEnabled) {
        // Use Zillow-specific package types (backend has placeholder handling)
        packageType = `${party}-affidavit`;
      } else {
        // Current behavior - only buyer is supported
        if (party === ePartyPurpose.buyer) {
          packageType = 'buyer-affidavit-spruce';
        } else {
          console.log(`Legacy ${party} affidavit not supported via ZDocs`);
          setGeneratingParty(null);
          return;
        }
      }

      const result = await generatePackageMutation({ orderId, packageType });
      if (checkForMutationSuccess(result, `sending ${party} package generation request`)) {
        setIsZDocLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 10_000));
        await dispatch(getAdminDocuments(orderId, allCategories));
      }
    } catch (error) {
      console.error('Error generating ZDocs package:', error);
    } finally {
      setIsZDocLoading(false);
      setGeneratingParty(null);
    }
  };

  const availablePartyPurpose =
    order.transaction.transaction_category === eTransactionCategory.Purchase
      ? [ePartyPurpose.buyer, ePartyPurpose.seller]
      : [ePartyPurpose.borrower];

  const fetchingGeneration: Record<string, boolean> = availablePartyPurpose.reduce(
    (acc, party) => ({
      ...acc,
      [party]: isAffidavitGenerating && generatingParty === party,
    }),
    {}
  );

  return (
    <Card title={'Affidavits'}>
      <ErrorMessage error={convertToError(generateAffidavitError)} />
      <AffidavitsTable
        onGenerate={onGenerateAffidavits}
        onGenerateViaZdocs={onGenerateViaZdocs}
        availablePartyPurpose={availablePartyPurpose}
        fetchingGeneration={fetchingGeneration}
        generatingParty={generatingParty}
      />
    </Card>
  );
};
